import { ColDef, GridApi, Column } from "ag-grid-community";
import { RowModelType } from "ag-grid-community";
import { ViewMode } from "../../../Reusables/components/ViewToggle/ViewToggle";

export type GridApiProps = {
  offset: number;
  limit: number;
  sortModel?: any;
  filterModel?: any;
  searchText?: string;
  sourceCode?: string;
  filter?: Record<
    string,
    string | number | boolean | string[] | number[] | undefined
  >;
  clientGroupCode?: number | null;
};

export type CygnusGridRef = {
  refreshGrid: (arg?: boolean) => void;
  getSelectedRows: () => any[];
  deselectAllRows: () => void;
  api?: GridApi;
  columnApi?: Column;
};

export type CygnusGridProps = {
  columnDefs: ColDef[];
  fetchData: (params: GridApiProps) => Promise<{
    records: any[];
    totalCount: number;
  }>;
  filterKeyMap?: Record<string, string>; // e.g. { clientGroupCode: 'Group', approvalStatus: 'Status' }
  rowSelection: "single" | "multiple" | undefined;
  dataIdentifier: string;
  searchText?: string;
  sourceCode?: string;
  filter?: Record<
    string,
    string | number | boolean | string[] | number[] | undefined
  >;
  preSelectedRows?: any[];
  pageSize?: number;
  maxBlocksInCache?: number;
  defaultColDef?: ColDef;
  cachedIsLoading?: boolean;
  floatingFilters?: boolean;
  onLoadingChange?: (isLoading: boolean) => void;
  onSelectionChange?: (selectedRows: any[]) => void;
  cachedData?: any[]; // passed in to load
  setCachedData?: (data: any[]) => void; // called when fresh data is fetched
  onCellValueChanged?: (item: any) => void;
  singleClickEdit?: boolean;
  suppressRowHoverHighlight?: boolean;
  rowModelType?: RowModelType;
  enableRowReorder?: boolean;
  onRowReorder?: Function;
  onFirstDataRendered?: (params: any) => void;
  onGridReady?: (params: any) => void;
  onRowSelected?: (params: any) => void;
  gridContext?: Record<string, any>;
  rowHeight?: number;
};

// date filter params (parses your cell text to Date for local client-side filtering UI)
export const dateFilterParams = {
  comparator: (filterLocalDateAtMidnight: Date, cellValue?: string) => {
    if (!cellValue) return -1;
    // cellValue is whatever your grid displays (e.g., "2024-11-20 13:20")
    const d = new Date(cellValue);
    const cellMidnight = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    if (cellMidnight < filterLocalDateAtMidnight) return -1;
    if (cellMidnight > filterLocalDateAtMidnight) return 1;
    return 0;
  },
};

/**
 * Generic factory for creating AG Grid filter value providers.
 *
 * @param items - Source array (e.g., users, partners, statuses)
 * @param selector - Function to extract the value to display in filter
 * @returns Function usable as AG Grid filterParams.values
 */
export const createValueProvider = <T>(
  items: T[] | undefined,
  selector: (item: T) => string | number | undefined
): GridValueProvider => {
  return (p: any) => {
    if (!items || items.length === 0) return p.success([]);
    const values = items
      .map((i) => selector(i))
      .filter((v): v is string | number => v !== undefined && v !== null)
      .map(String);
    p.success(values);
  };
};

/**
 * Common specialized providers
 */
export const createPartnerProvider = (partners: any[]) =>
  createValueProvider(partners, (p) => p.value);

export const createPurposeProvider = (purposes: any[]) =>
  createValueProvider(purposes, (p) => p.value ?? p.code);

export const createUsersProvider = (users: any[]) =>
  createValueProvider(users, (u) => u.UserFullName);

export const createClientTypesProvider = (clientTypes: any[]) =>
  createValueProvider(clientTypes, (t) => t.ReferenceDataValue);

export const createStatusProvider = (statuses: any[]) =>
  createValueProvider(statuses, (s) => s.ReferenceDataValue);

export const createLabelMap = <T>(
  items: T[],
  keySelector: (item: T) => string | number,
  labelSelector: (item: T) => string
): Map<string, string> => {
  return new Map(items.map((i) => [String(keySelector(i)), labelSelector(i)]));
};

// Add ALL filter keys (coming from CygnusGrid's filterKeyMap)
export const append = (key: string, val: any, params: any) => {
  if (val == null || val === "") return;
  if (Array.isArray(val)) {
    if (val.length === 0) return;
    params.set(key, JSON.stringify(val.map((v) => String(v).trim())));
  } else if (val instanceof Date) {
    params.set(key, val.toISOString());
  } else {
    params.set(key, String(val).trim());
  }
};

export type SavedGridFilters = {
  gridKey: string;
  userId: string;
  savedAt: string;
  viewMode?: ViewMode;
  gridModel: Record<string, any>;
  serverFilter: Record<string, any>;
};

/**
 * Build server filter payload from an AG Grid model using a filterKeyMap.
 * @param gridModel AG Grid filter model from api.getFilterModel()
 * @param filterKeyMap map of colId -> backend key
 */
export function buildServerFilterPayload(
  gridModel: Record<string, any>,
  filterKeyMap: Record<string, string>
) {
  const server: Record<string, any> = {};
  for (const [colId, model] of Object.entries(gridModel ?? {})) {
    const key = filterKeyMap[colId];
    if (!key || !model) continue;

    if (model.filterType === "set") {
      if (Array.isArray(model.values) && model.values.length) {
        server[key] = model.values;
      }
      continue;
    }
    if (model.filterType === "text") {
      if (model.filter != null && model.filter !== "")
        server[key] = String(model.filter);
      continue;
    }
    if (model.filterType === "number") {
      if (model.type === "inRange")
        server[key] = { from: model.filter, to: model.filterTo };
      else server[key] = model.filter;
      continue;
    }
    if (model.filterType === "date") {
      if (model.type === "inRange")
        server[key] = { from: model.dateFrom, to: model.dateTo };
      else server[key] = model.dateFrom;
      continue;
    }
  }
  return server;
}

export type AvailableFilterValuesByKey = Record<string, readonly unknown[]>;

/** Remove empty/duplicate filters and omit set filters containing every available value. */
export function normalizeServerFilters(
  filters: Record<string, any> | null | undefined,
  availableByKey: AvailableFilterValuesByKey = {}
) {
  const result: Record<string, any> = {};
  for (const [key, raw] of Object.entries(filters ?? {})) {
    if (raw == null || raw === "") continue;
    if (!Array.isArray(raw)) {
      result[key] = typeof raw === "string" ? raw.trim() : raw;
      continue;
    }
    const seen = new Set<string>();
    const selected = raw.filter((value) => {
      const normalized = String(value ?? "").trim();
      if (!normalized || seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    }).map((value) => typeof value === "string" ? value.trim() : value);
    if (!selected.length) continue;
    const available = Array.from(new Set((availableByKey[key] ?? []).map(String).map((value) => value.trim()).filter(Boolean)));
    if (available.length && available.every((value) => seen.has(value))) continue;
    result[key] = selected;
  }
  return result;
}

/**
 * Apply a full AG Grid filter model back to the UI.
 * @param gridApi grid api
 * @param gridModel model to apply. null clears all
 */
export function applyGridModelToUI(
  gridApi: any,
  gridModel: Record<string, any> | null
) {
  if (!gridApi) return;
  const model = gridModel && Object.keys(gridModel).length ? gridModel : null;
  gridApi.setFilterModel(model);
  gridApi.onFilterChanged();
}

/**
 * Merge server filters. Arrays union. Scalars override.
 * @param base base filter object
 * @param extra extra filter object
 */
export function mergeServerFilters(
  base: Record<string, any>,
  extra: Record<string, any>
) {
  const out: Record<string, any> = { ...(base ?? {}) };

  for (const [k, v] of Object.entries(extra ?? {})) {
    const existing = out[k];

    // If both sides are arrays, union them (for true multi-selects like partners)
    if (Array.isArray(existing) && Array.isArray(v)) {
      out[k] = Array.from(new Set([...existing, ...v]));
      continue;
    }

    // If existing is a scalar and extra is an array,
    // keep the scalar (user's current text filter) and ignore the old array.
    if (!Array.isArray(existing) && Array.isArray(v)) {
      continue;
    }

    // Otherwise, let extra override (normal scalar override behaviour)
    out[k] = v;
  }

  return out;
}


/**
 * Build a Set Filter model for a column.
 * @param values checked values
 */
export function buildSetFilterModel(values: string[]) {
  return values?.length ? { filterType: "set", values } : null;
}

export function setFilterValues(
  api: any,
  colId: string,
  values: string[] | null
) {
  const model = api.getFilterModel?.() ?? {};
  if (values && values.length) {
    model[colId] = { filterType: "set", values };
  } else {
    delete model[colId]; // clear
  }
  api.setFilterModel(model);
  api.onFilterChanged();
}

// stable, readable per-grid/user key
export function buildFilterParamName(gridKey: string, userId: string) {
  return `grid_filters.${gridKey}.${userId}`;
}

export function safeJsonParse<T = any>(s: string | null | undefined): T | null {
  if (!s) return null;
  try { return JSON.parse(s) as T; } catch { return null; }
}

// Accept any AG Grid-style Set Filter values provider
export type GridValueProvider<T = string> =
  (p: any) => void | T[] | Promise<T[]>; // void == callback style (params.success)

/**
 * Selected-first wrapper for Set Filter `values`.
 * Pass the column's colId and its base values provider/array.
 */
export function selectedFirstValuesProvider<T = string>(
  colId: string,
  base: T[] | ((p: any) => void | T[] | Promise<T[]>),
  getValue: (x: T) => string = (x: any) =>
    x && typeof x === "object" && "value" in x ? String(x.value) : String(x)
) {
  return (params: any) => {
    const model = params?.api?.getFilterModel?.() ?? {};
    const selected: string[] = model[colId]?.values?.map(String) ?? [];
    const selectedSet = new Set(selected);

    const forward = (vals: T[] | null | undefined) => {
      const list = Array.isArray(vals) ? vals.slice() : [];
      list.sort((a, b) => {
        const va = getValue(a);
        const vb = getValue(b);
        const aSel = selectedSet.has(va);
        const bSel = selectedSet.has(vb);
        if (aSel !== bSel) return aSel ? -1 : 1;
        return va.localeCompare(vb);
      });
      params.success(list);
    };

    try {
      if (Array.isArray(base)) {
        // base is static array
        return forward(base);
      }

      if (typeof base === "function") {
        // proxy success so callback-style providers get sorted too
        const proxied = {
          ...params,
          success: (vals: T[]) => forward(vals),
          fail: () => params.success([]),
        };

        const ret = base(proxied);

        // sync return: array
        if (Array.isArray(ret)) return forward(ret);
        // async return: promise
        if (ret && typeof (ret as Promise<T[]>).then === "function") {
          (ret as Promise<T[]>).then(forward).catch(() => params.success([]));
          return; // IMPORTANT: don't return a Promise to AG Grid
        }

        // callback-style: base will call proxied.success => handled above
        return;
      }

      // unexpected type
      params.success([]);
    } catch {
      params.success([]);
    }
  };
}
